import { supabase } from './supabase'

const EDGE_FUNCTION = 'process-recording-v2'

/**
 * Uploads an audio file to the temp bucket under the user's own uid folder,
 * then invokes the process-recording Edge Function which kicks off the
 * transcription pipeline and queues a summarization job.
 *
 * Returns the new call_records id on success.
 */
export async function uploadAudioAndProcess(args: {
  liverId: string
  file: File | Blob
  filename?: string
}): Promise<{ callId: string; storagePath: string }> {
  const { data: auth } = await supabase.auth.getUser()
  if (!auth?.user) throw new Error('Not signed in')
  const uid = auth.user.id

  const ext = (() => {
    const name =
      args.filename ?? ('name' in args.file ? (args.file as File).name : 'recording.webm')
    const m = /\.([a-z0-9]+)$/i.exec(name)
    return m ? m[1].toLowerCase() : 'webm'
  })()
  const fileId = crypto.randomUUID()
  const storagePath = `${uid}/${fileId}.${ext}`

  // Determine content type. Prefer the browser-reported MIME, but fall back
  // to an extension-based guess when the browser leaves it blank (which
  // happens on some Windows MIME registry configurations). The bucket's
  // allowed_mime_types is the final gate.
  const contentType = pickContentType(args.file, ext)

  // 1. Upload to Storage
  const { error: upErr } = await supabase.storage
    .from('call-audio-temp')
    .upload(storagePath, args.file, {
      contentType,
      upsert: false
    })
  if (upErr) throw new Error(`Storage upload failed: ${upErr.message}`)

  // 2. Invoke Edge Function (Deepgram only). Returns awaiting_summary on
  //    success — the Gateway will then summarize.
  const { data, error: fnErr } = await supabase.functions.invoke(EDGE_FUNCTION, {
    body: {
      liver_id: args.liverId,
      storage_path: storagePath,
      called_at: new Date().toISOString()
    }
  })
  if (fnErr) throw new Error(`Edge function failed: ${fnErr.message}`)
  const callId = (data as { call_id?: string })?.call_id
  if (!callId) throw new Error(`Unexpected Edge response: ${JSON.stringify(data)}`)

  return { callId, storagePath }
}

// Extension → MIME map. Values must match `call-audio-temp` bucket's
// allowed_mime_types (see migration `call_audio_temp_expand_mime_types`).
const EXT_TO_MIME: Record<string, string> = {
  webm: 'audio/webm',
  m4a: 'audio/mp4', // canonical; Chrome on Win may report audio/x-m4a — both are allowed
  mp4: 'audio/mp4',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  aac: 'audio/aac',
  flac: 'audio/flac'
}

function pickContentType(file: File | Blob, ext: string): string {
  const reported = 'type' in file ? (file as File).type : ''
  if (reported && reported.startsWith('audio/')) return reported
  const guess = EXT_TO_MIME[ext]
  if (guess) return guess
  // Last-resort fallback: the bucket allows application/octet-stream so the
  // upload still goes through; Deepgram sniffs the actual format from bytes.
  return 'application/octet-stream'
}
